<template>
  <LoadingElement v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>

    <!-- Top bar -->
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        firstElement="Mon"
        secondElement="profil"
        :coloredSecond="true"
      />
      <div class="pp-topbar">
        <ThemeToggle />
        <AccountToggle displayLogout="true" />
      </div>
    </div>

    <!-- Bannière mot de passe -->
    <div class="pp-banner rounded-xl mb-6" @click="changePassword">
      <div>
        <h2 class="pp-banner__title">Sécurisez votre compte</h2>
        <p class="pp-banner__sub">Mettez à jour votre mot de passe pour garder votre compte protégé</p>
      </div>
      <button class="pp-banner__btn">
        <v-icon size="15">mdi-lock-reset</v-icon>
        Changer le mot de passe
      </button>
    </div>

    <!-- Bloc principal -->
    <div class="pp-container">

      <!-- Header section -->
      <div class="pp-section-header">
        <div class="pp-section-header__left">
          <div class="pp-avatar">
            <span class="pp-avatar__letter">{{ avatarLetter }}</span>
          </div>
          <div>
            <h1 class="pp-username">{{ authStore.userInfos?.username || "—" }}</h1>
            <span class="pp-email">{{ authStore.userInfos?.email || "—" }}</span>
          </div>
        </div>
      </div>

      <div class="pp-divider" />

      <!-- Informations de base -->
      <div class="pp-section-title">Informations du compte</div>
      <div class="pp-info-grid">
        <div class="pp-info-row">
          <div class="pp-info-row__label">
            <v-icon size="14" class="pp-info-row__icon">mdi-account-outline</v-icon>
            Nom d'utilisateur
          </div>
          <span class="pp-info-row__value">{{ authStore.userInfos?.username || "—" }}</span>
        </div>
        <div class="pp-info-row">
          <div class="pp-info-row__label">
            <v-icon size="14" class="pp-info-row__icon">mdi-email-outline</v-icon>
            Adresse e-mail
          </div>
          <span class="pp-info-row__value">{{ authStore.userInfos?.email || "—" }}</span>
        </div>
      </div>
    </div>

    <!-- Stats de santé -->
    <div class="pp-container mt-4">
      <div class="pp-section-title mb-3">Statistiques de santé</div>
      <HealthStatsBloc />
    </div>

    <!-- Préférences & infos complémentaires -->
    <div class="pp-container mt-4">
      <div class="pp-prefs-header mb-3">
        <span class="pp-section-title">Mes préférences</span>
        <button class="pp-btn-edit" @click="router.push('/edit_preferences')">
          <v-icon size="13">mdi-pencil</v-icon>
          Modifier
        </button>
      </div>
      <Info_display_bloc :user="authStore.userInfos" />
    </div>

  </div>

  <!-- Snackbar -->
  <v-snackbar v-model="showSnack" :color="snackColor" :timeout="2000" location="bottom">
    {{ snackMessage }}
  </v-snackbar>

  <!-- Modal mot de passe -->
  <v-dialog v-model="showPasswordDialog" max-width="420">
    <div class="pp-modal">
      <div class="pp-modal__header">
        <div class="pp-modal__title">
          <v-icon class="pp-modal__icon">mdi-lock-reset</v-icon>
          Changer mon mot de passe
        </div>
        <button class="pp-modal__close" @click="showPasswordDialog = false">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="pp-modal__body">
        <p class="pp-modal__hint-top">
          Entrez votre adresse e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.
        </p>
        <v-text-field
          v-model="email"
          type="email"
          label="Votre e-mail"
          variant="outlined"
          density="compact"
          required
          prepend-inner-icon="mdi-email-outline"
          class="pp-modal__field"
        />
        <button
          class="pp-modal__submit"
          :disabled="loading"
          @click="onSubmit"
        >
          <v-icon size="15" class="mr-1">mdi-email-fast-outline</v-icon>
          {{ loading ? "Envoi…" : "Envoyer le lien" }}
        </button>
        <p v-if="done" class="pp-modal__success">
          Si un compte existe pour cet e-mail, un message vient d'être envoyé.
        </p>
        <p v-if="error" class="pp-modal__error">{{ error }}</p>
      </div>

      <div class="pp-modal__footer">
        <button class="pp-modal__ghost" @click="showPasswordDialog = false">Fermer</button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { ref, computed } from "vue";
import { getAuth, signOut } from "firebase/auth";
import { useRouter } from "vue-router";
import TopDeck from "../Utils/TopDeck.vue";
import LoadingElement from "../Animations/LoadingElement.vue";
import { useAuthStore } from "@/store/auth_store";
import Info_display_bloc from "./InfoDisplayBlock.vue";
import { requestPasswordReset } from "@/service/auth_service";
import HealthStatsBloc from "./HealthStatsBloc.vue";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";

export default {
  name: "ProfilPage",
  components: {
    LoadingElement,
    TopDeck,
    Info_display_bloc,
    HealthStatsBloc,
    ThemeToggle,
    AccountToggle,
  },
  setup() {
    const router = useRouter();
    const auth = getAuth();
    const authStore = useAuthStore();

    const showSnack = ref(false);
    const snackMessage = ref("");
    const snackColor = ref("success");

    const showPasswordDialog = ref(false);
    const email = ref("");
    const loading = ref(false);
    const done = ref(false);
    const error = ref("");

    const avatarLetter = computed(() => {
      const name = authStore.userInfos?.username || authStore.userInfos?.email || "?";
      return name.charAt(0).toUpperCase();
    });

    const changePassword = () => {
      showPasswordDialog.value = true;
    };

    async function onSubmit() {
      loading.value = true;
      error.value = "";
      try {
        await requestPasswordReset(email.value);
        done.value = true;
      } catch (e) {
        console.error("requestPasswordReset error", e);
        done.value = true;
      } finally {
        loading.value = false;
      }
    }

    const handleSignout = () => {
      signOut(auth)
        .then(() => { router.push("/login"); })
        .catch((err) => { console.error(err); });
    };

    return {
      authStore,
      showSnack,
      snackMessage,
      snackColor,
      showPasswordDialog,
      email,
      loading,
      done,
      error,
      avatarLetter,
      router,
      onSubmit,
      handleSignout,
      changePassword,
    };
  },
};
</script>

<style scoped>
/* ── Préférences header ──────────────────────────── */
.pp-prefs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.pp-btn-edit {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #1A1615;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.pp-btn-edit:hover { opacity: 0.85; }

/* ── Top bar ─────────────────────────────────────── */
.pp-topbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Bannière ────────────────────────────────────── */
.pp-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, rgb(var(--v-theme-accent)), rgba(255,255,255,0.55));
  color: white;
  cursor: pointer;
  gap: 12px;
}
.pp-banner__title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
}
.pp-banner__sub {
  font-size: 12px;
  margin: 0;
  opacity: 0.85;
}
.pp-banner__btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  color: rgb(var(--v-theme-accent));
  font-size: 12px;
  font-weight: 600;
  padding: 7px 16px;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.pp-banner__btn:hover { opacity: 0.88; }

/* ── Container principal ─────────────────────────── */
.pp-container {
  background: rgb(var(--v-theme-secondary));
  border-radius: 16px;
  padding: 20px 20px 24px;
}

/* ── Header profil ───────────────────────────────── */
.pp-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}
.pp-section-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* Avatar lettre */
.pp-avatar {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: rgb(var(--v-theme-accent));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pp-avatar__letter {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.pp-username {
  font-size: 17px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  margin: 0 0 2px;
}
.pp-email {
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.4);
}

/* ── Divider ─────────────────────────────────────── */
.pp-divider {
  height: 1px;
  background: rgba(var(--v-theme-text), 0.07);
  margin: 0 0 18px;
}

/* ── Titre de section ────────────────────────────── */
.pp-section-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--v-theme-text), 0.45);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 12px;
}

/* ── Lignes d'info ───────────────────────────────── */
.pp-info-grid {
  display: grid;
  gap: 8px;
}
.pp-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgb(var(--v-theme-tertiary));
  border: 0.5px solid rgba(var(--v-theme-text), 0.08);
}
.pp-info-row__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.55);
}
.pp-info-row__icon {
  color: rgba(var(--v-theme-text), 0.35);
}
.pp-info-row__value {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

/* ── Modal ───────────────────────────────────────── */
.pp-modal {
  background: rgb(var(--v-theme-tertiary));
  border-radius: 20px;
  border: 0.5px solid rgba(var(--v-theme-text), 0.1);
  overflow: hidden;
}
.pp-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 16px;
  border-bottom: 1px solid rgba(var(--v-theme-text), 0.07);
}
.pp-modal__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}
.pp-modal__icon {
  color: rgb(var(--v-theme-accent));
}
.pp-modal__close {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: none;
  background: rgba(var(--v-theme-text), 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(var(--v-theme-text), 0.6);
  transition: background 0.15s;
}
.pp-modal__close:hover { background: rgba(var(--v-theme-text), 0.12); }
.pp-modal__body {
  padding: 18px 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pp-modal__hint-top {
  margin: 0;
  font-size: 13px;
  color: rgba(var(--v-theme-text), 0.55);
  line-height: 1.5;
}
.pp-modal__field :deep(.v-field) {
  border-radius: 12px;
  background: rgb(var(--v-theme-secondary));
}
.pp-modal__submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  background: #1A1615;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s;
}
.pp-modal__submit:hover { opacity: 0.85; }
.pp-modal__submit:disabled { opacity: 0.5; cursor: not-allowed; }
.pp-modal__success {
  margin: 0;
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.6);
  text-align: center;
}
.pp-modal__error {
  margin: 0;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
  font-weight: 600;
}
.pp-modal__footer {
  padding: 12px 20px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid rgba(var(--v-theme-text), 0.07);
}
.pp-modal__ghost {
  background: transparent;
  border: 0.5px solid rgba(var(--v-theme-text), 0.18);
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-text), 0.6);
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.pp-modal__ghost:hover { background: rgba(var(--v-theme-text), 0.05); }
</style>
